<template>
  <div id="imageList">
    <!-- R4 Images -->
    <v-card max-height="50vh" style="overflow-y:auto">
      <v-toolbar color="grey lighten-3" height="40">
        <v-toolbar-title :class="titlePadding">
          R4 Images
        </v-toolbar-title>
        <v-spacer/>
        <v-row justify="center">
          <v-dialog v-model="showUploadDialog" persistent max-width="40vw">
            <template v-slot:activator="{ on }">
              <v-btn
                x-small
                color="secondary"
                v-on="on"
                class="text-right"
              >
                Upload
                <v-icon small>mdi-upload</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="text-h5">Upload Images</v-card-title>
              <v-card-text>
                <s3-multi-upload-form
                  :bucket="custImgBucket"
                  :prefix="s3ImgPrefix"
                  :image-reset-token="imageResetToken"
                  v-on:upload-success="handleUploadSuccess"
                  v-on:upload-failure="handleUploadFailure"
                ></s3-multi-upload-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green darken-1" text @click="showUploadDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </v-toolbar>
      <v-card-text>
        <v-text-field v-model="imageSearch" label="Search" dense hide-details class="ml-12" />
        <v-list :class="titlePadding" shaped dense>
          <v-list-item v-for="(img, i) in internalImages" :key="i" @click="copy(img.href)">
            <v-list-item-avatar size="30"><v-img :src="img.href"></v-img></v-list-item-avatar>
            <v-list-item-content><v-list-item-title>{{ extractFileName(img.pathname) }}</v-list-item-title></v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- EXTERNAL -->
    <v-card max-height="43vh" style="overflow-y:auto">
      <v-toolbar color="grey lighten-3" height="40">
        <v-toolbar-title :class="titlePadding">External Images</v-toolbar-title>
        <v-spacer/>
        <v-btn
          color="secondary"
          class="text-right"
          x-small
          @click="importNonR4Images"
          v-if="htmlExternalImages.length > 0"
        >
          Convert
          <v-icon small>mdi-swap-vertical-bold</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-list :class="listPadding" shaped dense style="overflow-y:auto">
          <v-list-item v-for="(img, i) in htmlExternalImages" :key="i" @click="copy(img.href)">
            <v-list-item-avatar size="30"><v-img :src="img.href"></v-img></v-list-item-avatar>
            <v-list-item-content><v-list-item-title>{{ extractFileName(img.pathname) }}</v-list-item-title></v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>

        <v-spacer/>
      </v-card-actions>
    </v-card>

    <v-overlay :value="lockScreen">
      <p>Copying Images</p>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import { CopyTextMixin } from '@/components/mixins'
import { EmailTemplateApi, R4UtilsApi, S3Client } from '@/services'
import { eqProps, uniqWith, filter } from 'ramda'
import SidebarPropsMixin from './SidebarPropsMixin'
import S3MultiUpload from '@/components/shared/S3MultiUpload'
const { v4: _uuid } = require('uuid')

export default {
  name: 'image-sidebar',
  mixins: [CopyTextMixin, SidebarPropsMixin],
  components: {
    [S3MultiUpload.name]: S3MultiUpload
  },
  props: {
    html: {
      type: String,
      required: true
    },
    // templateKey is uuid that distinguishes this campaign from other
    templateKey: {
      type: String,
      required: true
    }
  },
  data: function () {
    return {
      htmlDoc: null,
      imageSearch: '',
      htmlImages: [],
      s3Images: [],
      templateImageLocations: [],
      lockScreen: false,
      showUploadDialog: false,
      imageResetToken: _uuid()
    }
  },
  computed: {
    r4ImageHosts: function () {
      return this.templateImageLocations.map(il => il.host)
    },
    custImgBucket: function () {
      if (this.templateImageLocations.length > 0) {
        return this.templateImageLocations.find(til => til.editor === 'custom').bucket
      } else {
        return 'unknown'
      }
    },
    custImgHost: function () {
      return this.templateImageLocations.find(til => til.editor === 'custom').host
    },
    htmlExternalImages: function () {
      return this.htmlImages.filter(imgUrl => {
        return !this.r4ImageHosts.includes(imgUrl.host)
      })
    },
    htmlInternalImages: function () {
      return this.htmlImages.filter(imgUrl => {
        return this.r4ImageHosts.includes(imgUrl.host)
      })
    },
    internalImages: function () {
      const nodeComparator = eqProps('href')
      const mergedImages = [...this.htmlInternalImages, ...this.s3Images]
      const noopFilter = () => true
      const searchFilter = this.imageSearch ? (imgUrl) => imgUrl.href.includes(this.imageSearch) : noopFilter
      const blendedImages = filter(searchFilter)(uniqWith(nodeComparator)(mergedImages))
      return blendedImages.filter(imgUrl => {
        return this.r4ImageHosts.includes(imgUrl.host)
      })
    },
    s3ImgPrefix: function () {
      return `images/${this.templateKey}`
    }
  },
  watch: {
    html: {
      immediate: true,
      handler (newVal, oldVal) {
        const parser = new DOMParser()
        this.htmlDoc = parser.parseFromString(newVal, 'text/html')
      }
    },
    htmlDoc: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal) {
          const imgList = Array.from(newVal.body.getElementsByTagName('img')).map(node => new URL(node.src))
          // give back deduped list - no need to process images twice...
          const nodeComparator = eqProps('href')
          this.htmlImages = uniqWith(nodeComparator)(imgList)
        }
      }
    },
    custImageBucket: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal) {
          this.loadS3Images()
        }
      }
    }
  },
  methods: {
    extractFileName: function (imgPath) {
      return imgPath.toString().split('/').pop()
    },
    async importNonR4Images () {
      this.lockScreen = true
      try {
        const urlMap = this.buildUrlNameMap(this.htmlExternalImages)

        const copyResp = await R4UtilsApi.copyUrlToS3(urlMap)

        const successCopies = copyResp.data.filter(r => r.success)
        const failedCopies = copyResp.data.filter(r => !r.success)

        this.syncCopiedUrls(successCopies)

        if (failedCopies.length > 0) {
          failedCopies.forEach(failedCopy => {
            this.$flash({
              showClose: true,
              message: `Failed to copy ${this.extractFileName(failedCopy.from)}. ${failedCopy.message}`,
              type: 'mini',
              duration: 10000
            })
          })
        } else {
          this.$flash({
            showClose: true,
            message: 'Successfully copied images to R4',
            type: 'mini',
            duration: 3000
          })
        }
      } finally {
        this.lockScreen = false
      }
    },
    buildUrlNameMap: function (imgList) {
      return imgList.map(imgData => {
        const filename = this.extractFileName(imgData)
        const s3Key = `${this.s3ImgPrefix}/${filename}`
        const s3Loc = {
          bucket: this.custImgBucket,
          key: s3Key,
          href: `https://${this.custImgHost}/${s3Key}`
        }
        return { from: imgData.href, to: s3Loc }
      })
    },
    syncCopiedUrls: function (successfulCopies) {
      const newHtml = successfulCopies.reduce((targetHtml, copyInfo) => {
        return targetHtml.replaceAll(copyInfo.from, copyInfo.to.href)
      }, this.html)
      this.$emit('update:html', newHtml)
    },
    loadS3Images: async function () {
      this.$logger.info('Loading S3 Images')
      try {
        const s3Client = await S3Client.getService()
        const listParams = {
          Bucket: this.custImgBucket,
          Prefix: this.s3ImgPrefix
        }
        let allContents = []
        let continuationToken = null

        do {
          const s3Images = await s3Client.listObjectsV2({
            ...listParams,
            ContinuationToken: continuationToken
          }).promise()

          allContents = [...allContents, ...s3Images.Contents]
          continuationToken = s3Images.NextContinuationToken

          this.$logger.info('S3 Images batch received', s3Images.Contents.length)
        } while (continuationToken)

        this.$logger.info('Total S3 Images', allContents.length)
        this.s3Images = allContents.map(s3Item => {
          return new URL(`https://${this.custImgHost}/${s3Item.Key}`)
        })
      } catch (error) {
        this.$logger.error('Error retrieving s3 images', error.message)
      }
    },
    handleUploadSuccess: function (val) {
      this.loadS3Images()
      this.showUploadDialog = false
      this.imageResetToken = _uuid()
    },
    handleUploadFailure: function (val) {
      this.loadS3Images()
      this.showUploadDialog = false
      this.imageResetToken = _uuid()
      this.$flash({
        showClose: true,
        message: `Error uploading image to S3. ${val}`,
        type: 'mini',
        duration: 13000
      })
    }
  },
  mounted: function () {
    EmailTemplateApi.getTemplateImageLocation()
      .then(resp => {
        this.templateImageLocations = resp.data
      })
      .then(() => {
        this.$nextTick(() => {
          this.loadS3Images()
        })
      })
      .catch(error => {
        this.$flash({
          showClose: true,
          message: 'Error fetching image bucket location and resources Reload to try again.' + error.message,
          type: 'error',
          duration: 0
        })
      })
  }
}
</script>
